import { createAdminSession, readAdminError } from './admin-session';
import { getQrCodeDownloadUrl, groupLinkTargets, sortCampaignLinks } from './links-admin-format';
import type { CampaignLinkRow, CampaignLinksResponse, CampaignLinkTarget, LinkWindowCounts } from './links-admin-format';
import { linksAdminStrings as strings } from './links-admin-strings';

const statusSurface = requiredElement<HTMLElement>('status');
const createForm = requiredElement<HTMLFormElement>('create-link-form');
const labelInput = requiredElement<HTMLInputElement>('link-label');
const targetSelect = requiredElement<HTMLSelectElement>('link-target');
const createButton = requiredElement<HTMLButtonElement>('create-link-button');
const linksBody = requiredElement<HTMLTableSectionElement>('links-body');
const emptyState = requiredElement<HTMLElement>('links-empty');
const legend = requiredElement<HTMLElement>('links-legend');
let loadVersion = 0;
// A photograph's details dialog opens this page with ?target=/photo/<slug> to start a link for it.
let requestedTarget = new URLSearchParams(window.location.search).get('target');

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

function createElement<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    if (className != null) element.className = className;
    if (text != null) element.textContent = text;
    return element;
}

const adminSession = createAdminSession({
    signedOutMessage: strings.signedOut,
    loginFailedMessage: strings.loginFailed,
    requestFailedMessage: strings.requestFailed,
    setStatus,
    onAuthenticated: () => loadLinks(),
    onSignedOut: () => {
        linksBody.replaceChildren();
        targetSelect.replaceChildren();
        emptyState.hidden = true;
    }
});

async function loadLinks(message = ''): Promise<void> {
    const version = ++loadVersion;
    if (message.length === 0) setStatus(strings.loading);
    const response = await adminSession.request('/api/admin/links');
    if (version !== loadVersion) return;
    if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
    const payload = await response.json() as CampaignLinksResponse;
    if (version !== loadVersion) return;
    renderTargets(payload);
    renderLinks(payload.links);
    setStatus(message);
    if (requestedTarget != null) selectRequestedTarget(payload.targets);
}

function selectRequestedTarget(targets: readonly CampaignLinkTarget[]): void {
    const requested = requestedTarget?.toLowerCase();
    requestedTarget = null;
    const target = targets.find(item => item.path.toLowerCase() === requested);
    if (target == null) {
        setStatus(strings.targetUnavailable, 'error');
        return;
    }
    targetSelect.value = target.path;
    fillBlankLabel();
    labelInput.focus();
}

/** Starts a blank label from the chosen page's name, such as the photograph's title. */
function fillBlankLabel(): void {
    if (labelInput.value.trim().length > 0) return;
    labelInput.value = (targetSelect.selectedOptions[0]?.textContent ?? '').slice(0, labelInput.maxLength);
}

function renderTargets(payload: CampaignLinksResponse): void {
    const selected = targetSelect.value;
    const groups = groupLinkTargets(payload.targets).map(([kind, targets]) => {
        const group = createElement('optgroup');
        group.label = strings.targetGroups[kind] ?? kind;
        for (const target of targets) {
            const option = createElement('option', undefined, target.label);
            option.value = target.path;
            group.append(option);
        }
        return group;
    });
    targetSelect.replaceChildren(...groups);
    if (selected.length > 0 && payload.targets.some(target => target.path === selected)) targetSelect.value = selected;
}

function renderLinks(links: readonly CampaignLinkRow[]): void {
    emptyState.hidden = links.length > 0;
    emptyState.textContent = links.length > 0 ? '' : strings.empty;
    linksBody.replaceChildren(...sortCampaignLinks(links).map(createLinkRow));
}

function createLinkRow(link: CampaignLinkRow): HTMLTableRowElement {
    const row = createElement('tr', link.archivedUtc == null ? undefined : 'archived');

    const linkCell = createElement('td', 'links-link');
    const heading = createElement('div', 'links-label');
    heading.append(createElement('strong', undefined, link.label));
    if (link.archivedUtc != null) heading.append(' ', createElement('span', 'dashboard-card-badge', strings.archivedBadge));
    const address = createElement('div', 'links-address');
    address.append(createElement('code', undefined, link.url), createCopyButton(link));
    linkCell.append(
        heading,
        address,
        createElement('small', 'links-target', link.targetLabel == null ? strings.targetGone(link.targetPath) : strings.opens(link.targetLabel)));

    const actions = createElement('td', 'links-actions');
    const download = createElement('a', 'button secondary small-button', strings.downloadQr);
    download.href = getQrCodeDownloadUrl(link.code);
    download.download = `digitaljedwin-qr-${link.code}.png`;
    download.setAttribute('aria-label', strings.downloadQrLabel(link.label));
    const archive = createElement('button', 'secondary small-button', link.archivedUtc == null ? strings.archive : strings.restore);
    archive.type = 'button';
    archive.addEventListener('click', () => void setArchived(link, link.archivedUtc == null, archive)
        .catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error')));
    const remove = createElement('button', 'danger small-button', strings.delete);
    remove.type = 'button';
    remove.setAttribute('aria-label', strings.deleteLabel(link.label));
    remove.addEventListener('click', () => void deleteLink(link, remove)
        .catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error')));
    actions.append(download, archive, remove);

    row.append(linkCell, createCountsCell(link.scans), createCountsCell(link.visits), actions);
    return row;
}

function createCountsCell(counts: LinkWindowCounts): HTMLTableCellElement {
    const cell = createElement('td');
    const list = createElement('dl', 'links-counts');
    for (const [key, label] of strings.windows) {
        list.append(createElement('dt', undefined, label), createElement('dd', undefined, counts[key].toLocaleString('en-US')));
    }
    cell.append(list);
    return cell;
}

function createCopyButton(link: CampaignLinkRow): HTMLButtonElement {
    const button = createElement('button', 'secondary small-button', strings.copy);
    button.type = 'button';
    button.setAttribute('aria-label', strings.copyLabel(link.label));
    button.addEventListener('click', () => void (async () => {
        try {
            await navigator.clipboard.writeText(link.url);
            setStatus(strings.copied);
        } catch {
            setStatus(strings.copyFailed, 'error');
        }
        button.focus();
    })());
    return button;
}

async function setArchived(link: CampaignLinkRow, archived: boolean, button: HTMLButtonElement): Promise<void> {
    button.disabled = true;
    try {
        const response = await adminSession.request(`/api/admin/links/${encodeURIComponent(link.code)}/archived`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ archived })
        });
        if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
        await loadLinks(archived ? strings.archived(link.label) : strings.restored(link.label));
    } finally {
        button.disabled = false;
    }
}

async function deleteLink(link: CampaignLinkRow, button: HTMLButtonElement): Promise<void> {
    if (!window.confirm(strings.deleteConfirm(link.label))) return;
    button.disabled = true;
    try {
        const response = await adminSession.request(`/api/admin/links/${encodeURIComponent(link.code)}`, { method: 'DELETE' });
        if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
        await loadLinks(strings.deleted(link.label));
    } finally {
        button.disabled = false;
    }
}

targetSelect.addEventListener('change', fillBlankLabel);

createForm.addEventListener('submit', event => {
    event.preventDefault();
    void (async () => {
        createButton.disabled = true;
        setStatus(strings.creating);
        try {
            const label = labelInput.value.trim();
            const response = await adminSession.request('/api/admin/links', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ label, targetPath: targetSelect.value })
            });
            if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
            labelInput.value = '';
            await loadLinks(strings.created(label));
        } finally {
            createButton.disabled = false;
        }
    })().catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
});

legend.replaceChildren(...strings.legend.flatMap(([term, description]) => [
    createElement('dt', undefined, term),
    createElement('dd', undefined, description)
]));
void adminSession.initialize()
    .catch(error => setStatus(error instanceof Error ? error.message : strings.requestFailed, 'error'));
